import React from 'react';

var STATUS_ICON = { pending: '⏳', done: '✅', error: '❌', paused: '⏸' };

function DeploymentWindow(props) {
  var show         = props.show;
  var minimised    = props.minimised;
  var logs         = props.logs;
  var stages       = props.stages;
  var confirmation = props.confirmation;
  var tech         = props.tech;
  var apis         = props.apis || [];
  var onMinimise   = props.onMinimise;
  var onClose      = props.onClose;
  var onProceed    = props.onProceed;
  var onCancel     = props.onCancel;
  var onRetry      = props.onRetry;

  var logsEndRef = React.useRef(null);

  React.useEffect(function() {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  if (!show) return null;

  var techLabel   = tech === 'mule' ? 'MuleSoft' : tech === 'springboot' ? 'Spring Boot' : tech === 'dotnet' ? '.NET' : 'Deployment';
  var isDone      = apis.length > 0
    ? apis.every(function(a) { return a.status === 'done' || a.status === 'error'; })
    : stages.length > 0 && stages.every(function(s) { return s.status === 'done' || s.status === 'error'; });
  var hasError    = apis.length > 0
    ? apis.some(function(a) { return a.status === 'error'; })
    : stages.some(function(s) { return s.status === 'error'; });
  var activeStage = stages.find(function(s) { return s.status === 'active'; });
  var activeIdx   = stages.findIndex(function(s) { return s.status === 'active'; });

  if (minimised) {
    return (
      <div className="sh-dw-statusbar">
        <span className="sh-dw-statusbar-label">
          {'🚀 ' + techLabel + ' · ' + (activeStage ? ('Stage ' + (activeIdx + 1) + '/' + stages.length + ': ' + activeStage.name) : (isDone ? 'Complete' : 'Starting…'))}
          {!isDone && !confirmation && <span className="sh-dw-running-dot" />}
        </span>
        <button className="sh-dw-statusbar-expand" onClick={onMinimise}>▲ Expand</button>
      </div>
    );
  }

  return (
    <div className="sh-deploy-window">
      <div className="sh-dw-titlebar">
        <span className="sh-dw-title">{'🚀 Deployment · ' + techLabel}</span>
        <span className={'sh-dw-status' + (hasError ? ' sh-dw-status--error' : isDone ? ' sh-dw-status--done' : confirmation ? ' sh-dw-status--paused' : '')}>
          {hasError ? '❌ Failed' : isDone ? '✅ Complete' : confirmation ? '⏸ Paused' : (
            <React.Fragment>
              <span className="sh-dw-running-dot" />
              {' Running'}
            </React.Fragment>
          )}
        </span>
        <div className="sh-dw-controls">
          <button className="sh-dw-btn" onClick={onMinimise} title="Minimise">−</button>
          <button className="sh-dw-btn" onClick={onClose} title="Close">✕</button>
        </div>
      </div>

      {apis.length > 0 ? (
        <div className="sh-dw-api-cards">
          {apis.map(function(api, i) {
            return (
              <div key={i} className={'sh-dw-api-card sh-dw-api-card--' + api.status}>
                <div className="sh-dw-api-card-header">
                  <span className="sh-dw-api-card-name">{api.displayName || api.name}</span>
                  <span className="sh-dw-api-card-status">
                    {api.status === 'active'
                      ? <React.Fragment><span className="sh-dw-stage-spin sh-dw-stage-spin--sm" />{' deploying'}</React.Fragment>
                      : api.status === 'done'  ? '✅ Done'
                      : api.status === 'error' ? '❌ Failed'
                      : '⏳ Pending'}
                  </span>
                </div>
                {api.stages && api.stages.length > 0 && (
                  <div className="sh-dw-api-card-stages">
                    {api.stages.map(function(s, j) {
                      var cls = 'sh-dw-stage sh-dw-stage--' + s.status;
                      return (
                        <React.Fragment key={s.name}>
                          <div className={cls}>
                            <span className="sh-dw-stage-icon">
                              {s.status === 'active'
                                ? <span className="sh-dw-stage-spin" />
                                : (STATUS_ICON[s.status] || '⏳')}
                            </span>
                            <span className="sh-dw-stage-name">{s.name}</span>
                          </div>
                          {j < api.stages.length - 1 && <span className="sh-dw-stage-sep">›</span>}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        stages.length > 0 && (
          <div className="sh-dw-stage-strip">
            {stages.map(function(s, i) {
              var cls = 'sh-dw-stage';
              if (s.status === 'active')  cls += ' sh-dw-stage--active';
              if (s.status === 'done')    cls += ' sh-dw-stage--done';
              if (s.status === 'error')   cls += ' sh-dw-stage--error';
              if (s.status === 'paused')  cls += ' sh-dw-stage--paused';
              return (
                <React.Fragment key={s.name}>
                  <div className={cls}>
                    <span className="sh-dw-stage-icon">
                      {s.status === 'active'
                        ? <span className="sh-dw-stage-spin" />
                        : (STATUS_ICON[s.status] || '⏳')}
                    </span>
                    <span className="sh-dw-stage-name">{s.name}</span>
                  </div>
                  {i < stages.length - 1 && <span className="sh-dw-stage-sep">›</span>}
                </React.Fragment>
              );
            })}
          </div>
        )
      )}

      <div className="sh-dw-terminal">
        {logs.length === 0 && (
          <div className="sh-dw-log-line sh-dw-log--info">
            <span className="sh-dw-log-time">--:--:--</span>
            <span className="sh-dw-log-text">Starting deployment…</span>
          </div>
        )}
        {logs.map(function(l, i) {
          return (
            <div key={i} className={'sh-dw-log-line sh-dw-log--' + (l.type || 'info')}>
              <span className="sh-dw-log-time">{l.timestamp}</span>
              <span className="sh-dw-log-text">{l.text}</span>
            </div>
          );
        })}
        <div ref={logsEndRef} />
      </div>

      {confirmation && (
        <div className="sh-dw-confirmation">
          <div className="sh-deploy-confirmation-header">
            <span className="sh-deploy-pulse" />
            <span>Deployment Paused — Awaiting Your Verification</span>
          </div>
          <p className="sh-deploy-confirmation-msg">{confirmation.message}</p>
          {confirmation.details && (
            <div className="sh-deploy-confirmation-details">
              {confirmation.details.assetId    && <span>Asset: <strong>{confirmation.details.assetId}</strong></span>}
              {confirmation.details.instanceId && <span>Instance: <strong>{confirmation.details.instanceId}</strong></span>}
              {confirmation.details.cloudhubUrl && <span className="sh-deploy-cloudhub">CloudHub: <a href={confirmation.details.cloudhubUrl} target="_blank" rel="noopener noreferrer">{confirmation.details.cloudhubUrl}</a></span>}
              {confirmation.details.repoUrl    && <span>Repository: <a href={confirmation.details.repoUrl} target="_blank" rel="noopener noreferrer">{confirmation.details.repoUrl}</a></span>}
              {confirmation.details.branch     && <span>Branch: <strong>{confirmation.details.branch}</strong></span>}
            </div>
          )}
          {confirmation.details && confirmation.details.links && Object.keys(confirmation.details.links).length > 0 && (
            <div className="sh-deploy-confirmation-links">
              <span className="sh-deploy-links-label">Quick Links:</span>
              {Object.entries(confirmation.details.links).map(function(entry) {
                return (
                  <a key={entry[0]} href={entry[1]} target="_blank" rel="noopener noreferrer" className="sh-deploy-link">
                    {entry[0].replace(/([A-Z])/g, ' $1').replace(/^./, function(s) { return s.toUpperCase(); })}
                  </a>
                );
              })}
            </div>
          )}
          <div className="sh-deploy-confirmation-actions">
            <button className="sh-deploy-proceed-btn" onClick={onProceed}>▶ Proceed</button>
            {onCancel && <button className="sh-deploy-cancel-btn" onClick={onCancel}>✕ Cancel</button>}
          </div>
        </div>
      )}

    </div>
  );
}

export default DeploymentWindow;
